import { useQuery } from '@tanstack/react-query';
import type {
  PlayersResponseDto,
  PlayersSduiResponseDto,
  SearchOptionsResponseDto,
} from '@nomiss/sdk';
import {
  getPlayersByTeamCodes,
  getPlayersSdui,
  searchPlayers,
} from '@nomiss/services';

export const playersQueryKeys = {
  byTeamCodes: (teamCodes: string[]) =>
    ['players', 'by-team-codes', teamCodes] as const,
  sdui: (sportCode: string, teamCodes: string[]) =>
    ['players', 'sdui', sportCode, teamCodes] as const,
  search: (sportCode: string, query: string, limit: number) =>
    ['players', 'search', sportCode, query, limit] as const,
};

export const usePlayersByTeamCodes = (
  teamCodes: Array<string>,
  options?: { enabled?: boolean },
) => {
  const playersByTeamCodesQuery = useQuery<PlayersResponseDto>({
    queryKey: playersQueryKeys.byTeamCodes(teamCodes),
    queryFn: () => getPlayersByTeamCodes(teamCodes),
    ...options,
  });

  return { playersByTeamCodesQuery };
};

export const usePlayersSdui = (
  sportCode: string,
  teamCodes: Array<string>,
  options?: { enabled?: boolean },
) => {
  const playersSduiQuery = useQuery<PlayersSduiResponseDto>({
    queryKey: playersQueryKeys.sdui(sportCode, teamCodes),
    queryFn: () => getPlayersSdui(sportCode, teamCodes),
    ...options,
  });

  return { playersSduiQuery };
};

export const usePlayersSearch = (
  sportCode: string,
  query: string,
  options?: { enabled?: boolean; limit?: number },
) => {
  const limit = options?.limit ?? 20;
  const enabled = (options?.enabled ?? true) && sportCode.length > 0;

  const playersSearchQuery = useQuery<SearchOptionsResponseDto>({
    queryKey: playersQueryKeys.search(sportCode, query, limit),
    queryFn: () =>
      searchPlayers({
        sportCode,
        query: query.length > 0 ? query : undefined,
        limit,
      }),
    enabled,
  });

  return { playersSearchQuery };
};
